import type { SteelOrderQualityCheckStatusEnum } from '@mbfab/client-api'
import { isSome, steelOrderQualityCheckStatusLabels } from '@mbfab/shared'
import dayjs from 'dayjs'
import { useState } from 'react'

import { QualityCheckStatusLabel } from './status-badge'

import { CompactFormSelect } from '@/components/ui/form'
import { cn } from '@/lib/utils'

// select の value（string）を対応する enum へ、型アサーションなしで検証する。
// ラベルの定義元は @mbfab/shared の steelOrderQualityCheckStatusLabels（filter-bar.tsx の
// QUALITY_CHECK_STATUS_LABELS と二重管理しない）。そのキー集合を許容値の正とする
// （filter-bar.tsx の isKeyOf と同じ考え方）
function isQualityCheckStatus(value: string): value is SteelOrderQualityCheckStatusEnum {
  return Object.keys(steelOrderQualityCheckStatusLabels).includes(value)
}

// 品管照合セルの列幅（orders-table.tsx の colgroup で使う）。
// バッジ + 更新日時（YYYY/MM/DD HH:mm）+ セレクト(w-24) + 「更新失敗」（常時領域確保）を横並びで
// whitespace-nowrap 表示するため、余裕を持って 440px を確保する。
// 240px では実際に右端をはみ出すことを OrdersTable.stories.tsx の
// 「品管照合セルは列幅に収まる」でミューテーションテスト済み（列幅を 240px に戻すと
// セレクト・更新失敗表示の右端が table の右端を超えて検知できる）。
// この呼び出し元の div（whitespace-nowrap のラッパー）自体は table-fixed の列幅に合わせて
// 縮むだけで、縮みきらない子要素（幅固定のセレクト等）はラッパーの外側へそのままはみ出すため、
// ラッパー自身の getBoundingClientRect() だけを見ても収まっているように誤認する
// （はみ出し検知は子要素側の座標で行う必要がある。上記テストのコメント参照）。
// Tailwind の任意値クラスはソースを静的スキャンして CSS を生成するため、テンプレートリテラルで
// `w-[${px}px]` のように動的生成すると検出されず CSS が出力されない。文字列定数として
// export し、呼び出し元もこの定数をそのまま className に渡すことで、リテラル文字列を
// この1箇所にまとめつつ静的スキャンにも引っかかるようにする
export const QUALITY_CHECK_STATUS_CELL_WIDTH_CLASS = 'w-[440px]'

// 品管照合ステータスのセル（ラベル + 更新日時 + 3値セレクト）。発注一覧（orders-table.tsx）の
// 明細展開行と、発注詳細（items-table.tsx）の発注明細テーブルの**両方から使う共通部品**
// （20260907_01: 一覧の明細展開行に品管照合ステータス列を追加。docs/develop/client_ui/README.md
// 「このガイドラインを更新するとき」の「共通部品を変えたら呼び出し元をそろえる」ため両画面をこの部品に揃える）。
// testid・aria-label は「参照番号 + 明細の rank」で組み立てる。rank は発注内で明細を一意に
// 特定できる値で、一覧・詳細のどちらでも同じ明細なら同じ testid になる
export function QualityCheckStatusCell({
  referenceNumber,
  item,
  onUpdateItemQualityCheckStatus,
}: {
  referenceNumber: string
  item: {
    id: string
    rank: number
    qualityCheckStatus: SteelOrderQualityCheckStatusEnum
    qualityCheckUpdatedAt: string | null
  }
  onUpdateItemQualityCheckStatus: (itemId: string, status: SteelOrderQualityCheckStatusEnum) => Promise<boolean>
}) {
  const [pending, setPending] = useState(false)
  const [failed, setFailed] = useState(false)

  const handleChange = async (nextStatus: SteelOrderQualityCheckStatusEnum) => {
    setPending(true)
    setFailed(false)
    const ok = await onUpdateItemQualityCheckStatus(item.id, nextStatus)
    setPending(false)
    setFailed(!ok)
  }

  return (
    <div
      className="flex items-center gap-2 whitespace-nowrap"
      data-testid={`item-quality-check-${referenceNumber}-${item.rank}`}
    >
      <span data-testid={`item-quality-check-badge-${referenceNumber}-${item.rank}`}>
        <QualityCheckStatusLabel status={item.qualityCheckStatus} />
      </span>
      {isSome(item.qualityCheckUpdatedAt) && (
        <span className="text-xs text-muted-foreground">
          {dayjs(item.qualityCheckUpdatedAt).tz('Asia/Tokyo').format('YYYY/MM/DD HH:mm')}
        </span>
      )}
      <CompactFormSelect
        className="w-24"
        aria-label={`${referenceNumber} ${item.rank}行目の品管照合ステータスを変更`}
        disabled={pending}
        value={item.qualityCheckStatus}
        data-testid={`item-quality-check-select-${referenceNumber}-${item.rank}`}
        onChange={(e) => {
          const { value } = e.target
          if (!isQualityCheckStatus(value)) {
            return
          }
          void handleChange(value)
        }}
      >
        {Object.entries(steelOrderQualityCheckStatusLabels).map(([value, label]) => (
          <option key={value} value={value}>
            {label}
          </option>
        ))}
      </CompactFormSelect>
      {/* 失敗時だけ出現させると横幅が変わってレイアウトが飛ぶため、常時レンダーして
          visibility で出し分ける（幅は列幅の見積もりに含めて確保済み。2026/09/07 レビュー指摘） */}
      <span
        className={cn('text-xs text-destructive', !failed && 'invisible')}
        aria-hidden={!failed}
        data-testid={`item-quality-check-failed-${referenceNumber}-${item.rank}`}
      >
        更新失敗
      </span>
    </div>
  )
}
